import { ScheduleModel } from '@maxpreps/apiserviceclients/apiModels';
import { GetScheduleSeasonsAsync } from '@maxpreps/apiserviceclients/clients/GatewayWebServiceClient';
import { ROLE } from '@maxpreps/shared/constants/authentication';
import {
    SUPPORT_COACHES_TEAM_ADMINS,
    SUPPORT_CORRECTIONS,
    SUPPORT_SCHEDULE_MANAGEMENT
} from '@maxpreps/shared/constants/supportArticles';
import { getPrintScheduleUrl } from '@maxpreps/shared/constants/urls';
import useTeamContext from '@maxpreps/shared/contexts/TeamContext';
import { pluralize } from '@maxpreps/shared/functions/primitiveHelpers/stringHelpers';
import { getWebFormsTeamAdminUrl } from '@maxpreps/shared/functions/utils/getWebFormsAdminUrl';
import {
    getShotChart,
    getStatSheet
} from '@maxpreps/shared/functions/utils/printableGameDayForms';
import { hasPermission } from '@maxpreps/shared/functions/utils/userUtils';
import useIsAdmin from '@maxpreps/shared/hooks/useIsAdmin';
import { useIsMobile } from '@maxpreps/shared/hooks/useIsScreenSize';
import { IConfigurableLink } from '@maxpreps/shared/models/configurableLink';
import { useUserRoles } from '@maxpreps/shared/redux/user/userHooks';
import React from 'react';
import { useSeasonPickerItems } from '../../hooks/useSeasonPickerItems';
import getIsPublishedSportSeason from '../../utils/getIsPublishedSportSeason';
import TeamHeaderActions from '../shared/TeamHeaderActions';
import { getProgramCanonicalUrl, getTeamUrl } from './../../utils/navigation';
import { Page } from './../../utils/pages';
import useScheduleContext from './ScheduleContext';

const getMenuItems = (
  teamContext,
  scheduleContext,
  roles,
  isAdmin,
  isMobile
) => {
  const { data, teamSeasonPickerData } = teamContext;

  const {
    teamId,
    allSeasonId,
    sportSeasonId,
    canonicalUrl,
    level,
    sport,
    gender
  } = data;

  const {
    isEligibleForPrint,
    hasCorrectAScoreMode,
    setIsCorrectAScoreMode,
    isCorrectAScoreMode
  } = scheduleContext;

  const programCanonicalUrl = getProgramCanonicalUrl(
    teamSeasonPickerData,
    level
  );

  const { scheduleModel: scheduleModelKey, contestName } =
    teamContext.sportData as never as {
      scheduleModel: keyof ScheduleModel;
      contestName: string;
    };

  const isVarsityCoreSport =
    scheduleModelKey === ScheduleModel[ScheduleModel.Standard] &&
    level === 'Varsity';

  const menuItems: IConfigurableLink[] = [];

  if (isEligibleForPrint) {
    menuItems.push({
      text: 'Print Schedule',
      as: 'a',
      asOptions: {
        href: getPrintScheduleUrl(teamId, sportSeasonId),
        target: '_blank'
      }
    });
  }

  if (!isAdmin) {
    menuItems.push({
      text: 'Submit Schedule Correction',
      as: 'a',
      asOptions: {
        href: SUPPORT_CORRECTIONS,
        target: '_blank'
      }
    });

    if (hasCorrectAScoreMode) {
      menuItems.push({
        text: `${isCorrectAScoreMode ? 'Cancel' : 'Submit'} Score Correction`,
        as: 'button',
        asOptions: {
          onClick: () => {
            setIsCorrectAScoreMode(!isCorrectAScoreMode);
          }
        }
      });
    }

    if (isVarsityCoreSport) {
      menuItems.push({
        text: `${contestName} History`,
        as: 'a',
        asOptions: {
          href: getTeamUrl(Page.GameHistory, programCanonicalUrl as string)
        }
      });
    }

    // at time of writing (6/2)
    if (
      !isMobile &&
      getIsPublishedSportSeason(sportSeasonId, teamSeasonPickerData)
    ) {
      menuItems.push({
        text: 'Full Calendar',
        as: 'a',
        asOptions: {
          href: getTeamUrl(Page.Calendar, canonicalUrl)
        }
      });
    }
  }

  if (isAdmin) {
    menuItems.push({
      text: `Print ${contestName} Programs`,
      as: 'a',
      asOptions: {
        href: getWebFormsTeamAdminUrl(
          '/admin/coach/utility/frame_gameprograms.aspx',
          teamId,
          allSeasonId,
          sportSeasonId
        )
      }
    });

    menuItems.push({
      text: 'Manage Full Calendar',
      as: 'a',
      asOptions: {
        href: getWebFormsTeamAdminUrl(
          '/admin/coach/calendar/view.aspx',
          teamId,
          allSeasonId,
          sportSeasonId
        )
      }
    });

    if (hasPermission([{ roleId: ROLE.standardAdmin }], roles)) {
      menuItems.push({
        text: 'Export Stats',
        as: 'a',
        asOptions: {
          href: getWebFormsTeamAdminUrl(
            `/admin/controls/stats/handlers/exportallstats.ashx?schoolid=${teamId}&ssid=${sportSeasonId}`,
            teamId,
            allSeasonId,
            sportSeasonId
          ),
          download: true
        }
      });
    }

    menuItems.push({
      text: 'Schedule Management Help',
      as: 'a',
      asOptions: {
        href: SUPPORT_SCHEDULE_MANAGEMENT,
        target: '_blank'
      }
    });

    menuItems.push({
      text: 'Coach Support',
      as: 'a',
      asOptions: {
        href: SUPPORT_COACHES_TEAM_ADMINS,
        target: '_blank'
      }
    });

    menuItems.push({
      text: `Find ${pluralize(contestName)}`,
      as: 'a',
      asOptions: {
        href: getWebFormsTeamAdminUrl(
          '/admin/coach/directories/gameswanted.aspx',
          teamId,
          allSeasonId,
          sportSeasonId
        )
      }
    });

    const statSheet = getStatSheet(gender, sport);
    if (statSheet) {
      menuItems.push({
        text: `Printable Stat Sheet`,
        as: 'a',
        asOptions: {
          href: statSheet.href,
          download: statSheet.fileName
        }
      });
    }

    const shotChart = getShotChart(sport);
    if (shotChart) {
      menuItems.push({
        text: `Printable Shot Chart`,
        as: 'a',
        asOptions: {
          href: shotChart.href,
          download: shotChart.fileName
        }
      });
    }
  }

  return menuItems;
};

const ScheduleHeaderActions: React.FC = () => {
  const isAdmin = useIsAdmin();
  const isMobile = useIsMobile();
  const teamContext = useTeamContext();
  const roles = useUserRoles();
  const scheduleContext = useScheduleContext();
  const seasonPickerItems = useSeasonPickerItems(
    GetScheduleSeasonsAsync,
    Page.Schedule
  );

  const { isEligibleForPrint } = scheduleContext;

  const { teamId, sportSeasonId, year } = teamContext.data;

  const menuItems = getMenuItems(
    teamContext,
    scheduleContext,
    roles,
    isAdmin,
    isMobile
  );

  return (
    <TeamHeaderActions
      printHref={
        (isEligibleForPrint && getPrintScheduleUrl(teamId, sportSeasonId)) ||
        undefined
      }
      seasonPickerItems={seasonPickerItems}
      seasonPickerText={isMobile ? year : `20${year}`}
      menuItems={menuItems}
    />
  );
};

export default ScheduleHeaderActions;
